/**
 * IncomingOrders (index.jsx)
 * 
 * \src\pages\IncomingOrders\index.jsx
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import SettingsIcon from "@mui/icons-material/Settings";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  FormControl, InputLabel 
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import IncomingOrdersTable from "../../components/IncomingOrders/IncomingOrdersTable";
import OrderDetailsDrawer from "../../components/IncomingOrders/OrderDetailsDrawer";
import OrderInspector from "../../components/IncomingOrders/OrderInspector";
import { GET_SUPPLIERORDER_STATUSES, INCOMING_ORDERS } from "../../graphql/queries/IncomingOrdersContext";
import columnConfig from "../../components/IncomingOrders/helpers/columnConfig";
import ColumnType from "../../components/IncomingOrders/helpers/ColumnType";
import useGetIncomingOrder from "../../components/IncomingOrders/hooks/useGetIncomingOrder";
import useUpdateIncomingOrder from "../../components/IncomingOrders/hooks/useUpdateIncomingOrder";
import { useSnackbar } from "../../hooks";
import StatusManagerDialog from "../../components/IncomingOrders/StatusManagerDialog";
import ActionConfirmDialog from "../../components/IncomingOrders/ActionConfirmDialog";
import { CONFIRM_ACTIONS } from "../../graphql/mutations";

import { getActionDoneMessages } from "../../components/IncomingOrders/helpers/actions";

// ---- Filter-typer (chips + meny) ----
const ORDER_TYPES = [
  { id: "LAGER", label: "Lager", emoji: "🏷" },
  { id: "TEXTIL", label: "Textil", emoji: "👕" },
  { id: "INDUSTRI", label: "Industri", emoji: "🏭" },
  { id: "TRYCK", label: "Tryck", emoji: "🖨" },
];

const TABLE_HEADERS = [
  ColumnType.ORDER_TYPE,
  ColumnType.SUPPLIER_ORDER_NO,
  ColumnType.SUPPLIER_ORDER_REFERENCE,
  //ColumnType.ORDER_ID,
  ColumnType.ORDER_REFERENCE,
  ColumnType.RECEIVER,
  ColumnType.ORDER_DATE,
  ColumnType.SUMMARY,
  ColumnType.DELIVERY_ADDRESS_2,
  ColumnType.DELIVERY_DATE,
  ColumnType.ORDER_STATUS,
  ColumnType.SOURCE,
  ColumnType.ROW_COUNT,
];

// ---- Debounce-hook för sökfältet ----
const useDebouncedValue = (value, delay = 400) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handle);
  }, [value, delay]);
  return debounced;
};

const IncomingOrders = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const snackbar = useSnackbar();

  const [search, setSearch] = useState("");
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [sortField, setSortField] = useState(columnConfig[ColumnType.SUPPLIER_ORDER_NO].sortField);
  const [ascending, setAscending] = useState(false);

  const [filterAnchor, setFilterAnchor] = useState(null);
  const [settingsAnchor, setSettingsAnchor] = useState(null);
  const [selectedStatusId, setSelectedStatusId] = useState("");
  const [inspectorMode, setInspectorMode] = useState("drawer"); // "right" | "drawer"
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [inspectorWidth, setInspectorWidth] = useState(600);

  const [tableDisplayMode, setTableDisplayMode] = useState("pagination"); // "pagination" | "infinite"
  const [tablePage, setTablePage] = useState(0);
  const tableRef = useRef(null);

  const debouncedSearch = useDebouncedValue(search, 400);
  const lastSearchedRef = useRef("");

  // Used to prevent the debounced search effect from running
  // when a manual search is triggered via Enter key
  const skipDebounceAfterEnterRef = useRef(false);
  const [showOrderStatusModal, setShowOrderStatusModal] = useState(false);

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [actionsToConfirm, setActionsToConfirm] = useState([]);

  const [confirmActions] = useMutation(CONFIRM_ACTIONS);

  const {
    getIncomingOrder,
    currentSupplierOrderId,
    refetchIncomingOrder,
    incomingOrder,
    incomingOrderLoading,
    incomingOrderError,
    updateCachedIncomingOrder
  } = useGetIncomingOrder();

  const {
    updateIncomingOrder,    
    updateResult: orderUpdateResult,
    updateLoading: orderUpdateLoading,
    updateError: orderUpdateError,
    resetUpdateResult
  } = useUpdateIncomingOrder();

  const queryParamsRef = useRef({
    offset: 0,
    pageSize: 10,
    sortField: columnConfig[ColumnType.SUPPLIER_ORDER_NO].sortField,
    sortDesc: true,
    searchTerm: "",
    supplierOrderStatusId: null
  });

  const { data, loading, fetchMore, refetch } = useQuery(INCOMING_ORDERS, {
    variables: {
      ...queryParamsRef.current
    },
    notifyOnNetworkStatusChange: true,
    skip: orders.length > 0
  });

  const updateQueryParams = useCallback(
    (update) => {
      queryParamsRef.current = {
        ...queryParamsRef.current,
        ...update,
      };
    },
    []
  );

  /** FETCH */
  const [getStatuses] = useLazyQuery(GET_SUPPLIERORDER_STATUSES);
  const [supplierOrderStatuses, setSupplOrderStatuses] = useState([]);

  useEffect(() => {    

    getStatuses().then((res) => {
      const data = res?.data?.incomingOrdersContext?.listOrderStatuses ?? [];
      setSupplOrderStatuses(data);
    });

  }, [getStatuses]);

  useEffect(() => {
    if (!orderUpdateResult) return;

    const { success, appliedActions = [], result, actionsToConfirm: newActionsToConfirm = [] } = orderUpdateResult;

    if (success) {
      console.info("** Order updated successfully");

      const actionMessages = getActionDoneMessages(appliedActions);

      let message = null;

      if (actionMessages.length > 0) {
        message = actionMessages.join(", ");
      }
      else if (
        lastPatchRef.current &&
        Object.keys(lastPatchRef.current).length > 0
      ) {
        message = "Ordern uppdaterades.";
      }

      if (message) snackbar.success(message);

      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.supplierOrderId === result.supplierOrderId
            ? {
              ...order,
              supplierOrderReference: result.supplierOrderReference,
              status: result.status,
            }
            : order
        )
      );

      updateCachedIncomingOrder(result);

      if (Array.isArray(newActionsToConfirm) &&
      newActionsToConfirm.some(a => a && a !== "NONE")) {
        setActionsToConfirm(newActionsToConfirm);
        setConfirmDialogOpen(true);
      }

    } else {
      console.info("** Order update failed");
      snackbar.error("Kunde inte uppdatera ordern!");
    }

    resetUpdateResult();
  }, [orderUpdateResult, snackbar, updateCachedIncomingOrder]);

  const lastPatchRef = useRef(null);

  const handleUpdateOrder = useCallback(
    ({ supplierOrderId, patch }) => {
      if (!supplierOrderId) return;

      lastPatchRef.current = patch;

      updateIncomingOrder(supplierOrderId, patch);
    },
    [updateIncomingOrder]
  );

  //
  // --- INITIAL LOAD ---
  //
  useEffect(() => {
    if (!data) return;

    const incoming = data.incomingOrdersContext?.incomingOrders;
    if (!incoming) return;

    setOrders(incoming.items);
    setTotalCount(incoming.totalResult);
  }, [data]);

  //
  // --- REFRESH (sök/sortering ändras) ---
  //
  const refresh = useCallback(() => {
    lastSearchedRef.current = queryParamsRef.current.searchTerm;

    refetch({
      ...queryParamsRef.current,
    }).then(({ data }) => {
      const incoming = data?.incomingOrdersContext?.incomingOrders;
      if (!incoming) return;

      setOrders(incoming.items);
      setTotalCount(incoming.totalResult);
    });
  }, [refetch]);

  //
  // --- INFINITE SCROLL ---
  //
  const loadMore = () => {
    if (orders.length >= totalCount) return;

    const { offset, pageSize } = queryParamsRef.current;

    updateQueryParams({
      offset: offset + pageSize,
    });

    const model = queryParamsRef.current;

    fetchMore({
      variables: {
        ...model
      }
    }).then(({ data }) => {
      const incoming = data?.incomingOrdersContext?.incomingOrders;

      if (!incoming) return;

      setOrders(prev => [...prev, ...incoming.items]);
      setTotalCount(incoming.totalResult);
    });
  };

  const handlePageChange = useCallback(
    (pageIndex) => {
      const { pageSize } = queryParamsRef.current;

      setTablePage(pageIndex);

      updateQueryParams({
        offset: pageIndex * pageSize,
      });

      refresh();
    },
    [refresh, updateQueryParams]
  );

  const handlePageSizeChange = useCallback(
    (pageSize) => {
      setTablePage(0);

      updateQueryParams({
        offset: 0,
        pageSize,
      });

      refresh();
    },
    [refresh, updateQueryParams]
  );

  const handleSearchKeyDown = (e) => {
    if (e.key !== "Enter") return;

    skipDebounceAfterEnterRef.current = true;

    if (search !== lastSearchedRef.current) {
      setTablePage(0);

      updateQueryParams({
        offset: 0,
        searchTerm: search,
      });
    }

    refresh();
  };

  const handleStatusFilterChange = (e) => {
    const value = e.target.value;

    setSelectedStatusId(value);

    const parsedValue = parseInt(value, 10);
    const isValidInt = !isNaN(parsedValue);

    updateQueryParams({
      offset: 0,
      supplierOrderStatusId: isValidInt ? parsedValue : null
    });

    refresh();
  };

  

  const handleSortChange = useCallback(
    (field, nextAscending) => {
      setTablePage(0);
      setSortField(field);
      setAscending(nextAscending);

      updateQueryParams({
        offset: 0,
        sortField: field,
        sortDesc: !nextAscending,
      });

      refresh();
    },
    [refresh, updateQueryParams]
  );

  const handleTableDisplayModeChange = useCallback(
    (mode) => {
      if (!!mode) {
        updateDisplayMode(mode);
        setTablePage(0);

        updateQueryParams({
          offset: 0,
        });

        refresh();
      }
    },
    [refresh, updateQueryParams]
  );

  useEffect(() => {
    // Skip debounced search when Enter has already triggered a manual refresh
    if (skipDebounceAfterEnterRef.current) {
      skipDebounceAfterEnterRef.current = false;
      return;
    }

    setTablePage(0);

    updateQueryParams({
      offset: 0,
      searchTerm: debouncedSearch,      
    });

    refresh();
  }, [debouncedSearch, refresh]);

  const handleShowOrderDetails = useCallback(
    ({ supplierOrderId }) => {
      if (!supplierOrderId) return;

      getIncomingOrder(supplierOrderId);

      if (inspectorMode === "right" && isDesktop) {
        setInspectorOpen(true);
        setDrawerOpen(false);
      } else {
        setDrawerOpen(true);
        setInspectorOpen(false);
      }
    },
    [inspectorMode, isDesktop]
  );

  // ---- Filter-chips / meny ----
  const toggleType = useCallback((id) => {
    setSelectedTypes((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const handleClearType = (id) => {
    setSelectedTypes((prev) => prev.filter((x) => x !== id));
  };

  const handleFilterMenuOpen = (event) => setFilterAnchor(event.currentTarget);
  const handleFilterMenuClose = () => setFilterAnchor(null);

  const getTypeVisual = (id) => {
    const type = ORDER_TYPES.find((t) => t.id === id);
    if (!type) return { label: id, emoji: "" };
    return type;
  };

  // ---- Inställningsmeny (kugghjul) ----
  const handleSettingsOpen = (event) => setSettingsAnchor(event.currentTarget);
  const handleSettingsClose = () => setSettingsAnchor(null);

  const handleInspectorModeChange = (event) => {
    const value = event.target.value; // "right" | "drawer"
    setInspectorMode(value);
    setSettingsAnchor(null);
    // Stänger ev. öppna paneler – nästa klick på rad öppnar i valt läge
    setInspectorOpen(false);
    setDrawerOpen(false);
  };

  const updateDisplayMode = (mode) => {
    setTableDisplayMode(mode);
    setSettingsAnchor(null);
    // Stänger ev. öppna paneler – nästa klick på rad öppnar i valt läge
    setInspectorOpen(false);
    setDrawerOpen(false);
  };

  // Helper to build grammatically correct hits label
  const buildHitsLabel = (count = totalCount, filters = selectedTypes.length) => {
    if (!count) return "Inga träffar";
    const orderWord = count === 1 ? "order" : "ordrar";
    const filterPart = filters > 0 ? ` med ${filters} ${filters === 1 ? "filter" : "filter"}` : "";
    return `Hittade ${count} ${orderWord}${filterPart}`;
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Topp-rad: status + chips + sök + filter + inställningar */}
      <Paper
        elevation={1}
        sx={{
          px: 1.5,
          pb: 1.5,
          pt: 1.25,
          mb: 2,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 1.5,
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
        }}
      >
        {/* Titel */}
        <Typography variant="h5" gutterBottom fontWeight={700} sx={{ mb: 0, pr: 2 }}>
          Inkomna ordrar
        </Typography>

        {/* Vänster del: Sök */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          sx={{ flex: 1, minWidth: 0 }}
        >
          {/* Sökfält */}
          <TextField
            size="small"
            placeholder="Sök ordernr, kund, mottagare."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            variant="outlined"
            sx={{
              minWidth: { xs: 140, sm: 200, md: 260 },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ mr: 0, pr: 0 }}>
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {/* Status filter */}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Leverantörsstatus</InputLabel>
            <Select
              value={selectedStatusId}
              label="Status"
              onChange={handleStatusFilterChange}
            >
              {/* Default empty option */}
              <MenuItem value="">
                Alla statusar
              </MenuItem>

              {supplierOrderStatuses.map((status) => (
                <MenuItem key={status.id} value={status.id}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {status.name}
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {/* Höger del: + status-chip + filter-chips + filter + kugghjul */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ flexShrink: 0 }}
        >
          {/* Status-chip: Totalt antal träffar (visuellt) */}
          <Tooltip title={
            `${buildHitsLabel(totalCount, selectedTypes.length)}` +
            (debouncedSearch ? ` · Sök: ${debouncedSearch}` : "")
          }>
            <Chip
              size="small"
              label={buildHitsLabel(totalCount, selectedTypes.length)}
              sx={{
                borderRadius: "16px",
                fontSize: 13,
                height: 30,
                "& .MuiChip-label": {
                  paddingTop: "1px",
                },
              }}
            />
          </Tooltip>

          {/* Valda typfilter som chips */}
          {selectedTypes.length === 0 ? (
            <Chip
              size="small"
              label="Alla typer"
              sx={{
                height: 30,
                fontSize: 13,
                backgroundColor: "#f7bd0299",
                color: "#000",
                "& .MuiChip-label": {
                  paddingTop: "1px",
                },
              }}
            />
          ) : (
            selectedTypes.map((id) => {
              const { label, emoji } = getTypeVisual(id);
              return (
                <Chip
                  key={id}
                  size="small"
                  label={`${emoji} ${label}`}
                  onDelete={() => handleClearType(id)}
                  sx={{
                    height: 30,
                    fontSize: 13,
                    backgroundColor: "#f7bd0299",
                    color: "#000",
                    "& .MuiChip-label": {
                      paddingTop: "1px",
                    },
                  }}
                />
              );
            })
          )}

          {/* Filter-ikon */}
          <Tooltip title="Filtrera på typ">
            <IconButton size="small" onClick={handleFilterMenuOpen}>
              <FilterListIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Kolumn-väljare: flyttad upp från tabellen */}
          <Tooltip title="Visa/dölj kolumner">
            <IconButton
              size="small"
              onClick={() => tableRef.current?.openColumnPanel()}
              sx={{ mr: 0.5 }}
            >
              <ViewColumnIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Kugghjul / inställningar */}
          <Tooltip title="Inställningar">
            <IconButton size="small" onClick={handleSettingsOpen}>
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Paper>

      {/* Filtermeny */}
      <Menu
        anchorEl={filterAnchor}
        open={Boolean(filterAnchor)}
        onClose={handleFilterMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {ORDER_TYPES.map((type) => {
          const checked = selectedTypes.includes(type.id);
          return (
            <MenuItem
              key={type.id}
              onClick={() => toggleType(type.id)}
              dense
            >
              <Checkbox edge="start" size="small" checked={checked} />
              <ListItemText primary={`${type.emoji} ${type.label}`} />
            </MenuItem>
          );
        })}
      </Menu>

      {/* Inställningsmeny (kugghjul) med radio-knappar */}
      <Menu
        anchorEl={settingsAnchor}
        open={Boolean(settingsAnchor)}
        onClose={handleSettingsClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid rgba(0,0,0,0.04)' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, textTransform: 'none' }}>
            Visa orderdetaljer
          </Typography>
          <ToggleButtonGroup
            value={inspectorMode}
            exclusive
            size="small"
            onChange={handleInspectorModeChange}
            aria-label="visningsläge"
          >
            <ToggleButton value="drawer" aria-label="Order details in a drawer" sx={{ textTransform: 'none' }}>
              I sidopanel
            </ToggleButton>
            <ToggleButton value="right" aria-label="Order details to the right" sx={{ textTransform: 'none' }}>
              Till höger om tabellen
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid rgba(0,0,0,0.04)' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, textTransform: 'none' }}>
            Visningsläge
          </Typography>
          <ToggleButtonGroup
            value={tableDisplayMode}
            exclusive
            size="small"
            onChange={(e, value) => handleTableDisplayModeChange(value)}
            aria-label="visningsläge"
          >
            <ToggleButton value="pagination" aria-label="pagination" sx={{ textTransform: 'none' }}>
              Sidvisning
            </ToggleButton>
            <ToggleButton value="infinite" aria-label="infinite" sx={{ textTransform: 'none' }}>
              Oändlig rullning
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Button onClick={n => setShowOrderStatusModal(true) }>OrderStatus</Button>
      </Menu>

      {/* Tabell + ev. inspector till höger */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "stretch",
          gap: 2,
          mt: 1,
        }}
      >
        {/* Tabell-del */}
        <Box
          sx={{
            flex:
              inspectorMode === "right" && inspectorOpen && isDesktop
                ? `1 1 calc(100% - ${inspectorWidth}px - 16px)`
                : "1 1 100%",
            minWidth: 0,
          }}
        >
          <IncomingOrdersTable
            columnConfig={columnConfig}
            orders={orders ?? []}
            totalRowCount={totalCount ?? 0}
            loading={loading}
            headers={TABLE_HEADERS}
            ascending={ascending}
            sortField={sortField}
            onLoadMore={loadMore}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onShowOrderDetails={handleShowOrderDetails}
            onSortChange={handleSortChange}

            displayMode={tableDisplayMode}
            onDisplayModeChange={handleTableDisplayModeChange}

            page={tablePage}
            ref={tableRef}
          />
        </Box>

        {/* Inspector till höger */}
        {inspectorMode === "right" && inspectorOpen && isDesktop && (
          <Box
            sx={{
              //flex: `0 0 ${inspectorWidth}px`,
              //minWidth: 600,
              maxWidth: 900,
              position: "relative",
            }}
          >
            <OrderInspector
              order={incomingOrder}
              orderError={incomingOrderError}
              orderLoading={incomingOrderLoading}
              orderUpdating={orderUpdateLoading}
              orderUpdateError={orderUpdateError}
              width={inspectorWidth}
              onWidthChange={setInspectorWidth}
              onClose={() => setInspectorOpen(false)}
              onUpdateOrder={handleUpdateOrder}
              onOpenStatusManager={ n=> setShowOrderStatusModal (true)}
            />
          </Box>
        )}
      </Box>

      {/* Drawer med detaljer (vid "sidopanel" eller på mobil)*/}
      <OrderDetailsDrawer
        open={drawerOpen}
        order={incomingOrder}
        orderError={incomingOrderError}
        orderLoading={incomingOrderLoading}
        orderUpdating={orderUpdateLoading}
        orderUpdateError={orderUpdateError}
        onClose={() => setDrawerOpen(false)}
        onUpdateOrder={handleUpdateOrder}
        onOpenStatusManager={n => setShowOrderStatusModal(true)}
      />

      <ActionConfirmDialog
        open={confirmDialogOpen}
        order={incomingOrder}
        actions={actionsToConfirm ?? []}
        onClose={() => {
          setConfirmDialogOpen(false);
          setActionsToConfirm([]);
        }}
        onConfirm={async (payload) => {
          setConfirmDialogOpen(false);
          setActionsToConfirm([]);

          const supplierOrderId = incomingOrder?.supplierOrderId;
          if (!supplierOrderId) return;

          if (!Array.isArray(payload) || payload.length === 0) return;

          try {
            const performActions = payload
              .filter(item => item?.type)
              .map(item => {
                const emails =
                  Array.isArray(item.recipients)
                    ? item.recipients
                    : typeof item.recipient === "string"
                      ? item.recipient.split(/[;,]/).map(x => x.trim()).filter(Boolean)
                      : [];

                return {
                  action: item.type,
                  emailOverride: emails,
                };
              });

            if (performActions.length === 0) return;

            const { data } = await confirmActions({
              variables: {
                supplierOrderId,
                performActions,
              },
            });

            const success =
              data?.incomingOrders?.confirmActions;

            if (success) {
              const doneMessages = getActionDoneMessages(
                performActions.map(a => a.action)
              );

              if (doneMessages.length) {
                snackbar.success(doneMessages.join(" · "));
              }
              refetchIncomingOrder();
            } else {
              snackbar.error("Kunde inte utföra åtgärden.");
            }


          } catch (err) {
            console.error("Action execution failed:", err);
            snackbar.error("Kunde inte utföra åtgärden.");
          }
        }}
      />

      <StatusManagerDialog statuses={supplierOrderStatuses} open={showOrderStatusModal} onChange={setSupplOrderStatuses} onClose={n => setShowOrderStatusModal(false)} />
    </Box>
  );
};

IncomingOrders.displayName = "IncomingOrders";

export default IncomingOrders;
